import { useContext } from 'react';
import { Paper, Title, Group, Text, Box, Flex, Skeleton } from '@mantine/core';
import { Sentiment } from 'interfaces/Reporting';
import { sentimentColors } from 'constants/reporting';
import DonutChart from './DonutChart';
import { getValidSentiments, sentimentLabels } from '../utils';
import ReportContext from 'context/ReportContext';

import classes from './Sentiments.module.scss';
import { convertDecimalToPercentage } from 'utils/number';
import SentimentSmiley from '../components/CallHistoryTable/SetimentSmiley';

const Sentiments = () => {
  const {
    data: { callDetails, isLoadingCallDetails },
  } = useContext(ReportContext);
  const sentiments = callDetails.sentiment;
  const validSentiments = getValidSentiments(sentiments);

  const chartData = [
    {
      label: 'Positive',
      value: convertDecimalToPercentage(validSentiments[Sentiment.POSITIVE]),
      color: sentimentColors[Sentiment.POSITIVE],
    },
    {
      label: 'Neutral',
      value: convertDecimalToPercentage(validSentiments[Sentiment.NEUTRAL]),
      color: sentimentColors[Sentiment.NEUTRAL],
    },
    {
      label: 'Negative',
      value: convertDecimalToPercentage(validSentiments[Sentiment.NEGATIVE]),
      color: sentimentColors[Sentiment.NEGATIVE],
    },
  ];

  return (
    <Box w="100%">
      <Title size="md" mb="lg" mt="sm" fw={600}>
        Sentiments
      </Title>
      <Paper withBorder p="lg" radius="md" bg="var(--bg-color)">
        <Flex
          py="xl"
          gap="xl"
          direction={{ base: 'column', md: 'row' }}
          wrap="wrap"
        >
          <Group justify="center" align="center" flex={1}>
            <DonutChart data={chartData} thickness={45} />
          </Group>
          <Box flex={1}>
            <Flex gap="md" mb={{ base: 'md', lg: 'xl' }} align="center">
              <Text>Overall sentiment:</Text>
              {isLoadingCallDetails ? (
                <Skeleton w={26} h={26} radius="xl" m="2px" />
              ) : (
                <SentimentSmiley callSentiment={validSentiments} size={30} />
              )}
            </Flex>
            <Flex align="center" gap={{ base: 'md', lg: 'xl' }} mb="xs">
              {Object.entries(validSentiments).map(([key]) => (
                <Flex
                  key={key}
                  align="center"
                  direction={{ base: 'row', lg: 'column' }}
                  gap={{ base: '0.3rem', lg: '0.3rem' }}
                >
                  <Box
                    w={{ base: 10, lg: 12 }}
                    h={{ base: 10, lg: 12 }}
                    style={{
                      backgroundColor: sentimentColors[key as Sentiment],
                    }}
                  />
                  <Text
                    className={classes.label}
                    size="sm"
                    c="dimmed"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {sentimentLabels[key as Sentiment]}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Box>
        </Flex>
        <Flex justify="space-between" px={{ xl: '4rem' }} mt={{ lg: 'sm' }}>
          {Object.entries(validSentiments).map(([key, data]) => (
            <Flex key={key} direction="column" gap={4}>
              <Text
                size="sm"
                c="dimmed"
                className={classes.label}
                style={{ textTransform: 'capitalize' }}
              >
                {sentimentLabels[key as Sentiment]}
              </Text>
              <Text size="1.5rem" fw={700} className={classes.percentage}>
                {convertDecimalToPercentage(data)}%
              </Text>
            </Flex>
          ))}
        </Flex>
      </Paper>
    </Box>
  );
};

export default Sentiments;
